import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { MoChip, MoExternalLink, MoSpacing } from '@motivo/guanyin/src/components';
import { ResultOf } from ':src/graphql';
import { organizationAdminDetailsByIdQuery } from '../../graphql/queries';
import { Section, SectionItem } from '../Section';
import ZeusOrganizationLink from '../shared/ZeusOrganizationLink';

type OrganizationAdmin = NonNullable<
  ResultOf<typeof organizationAdminDetailsByIdQuery>['organizationAdminById']
>;

interface OrganizationSectionProps {
  organizationAdmin: OrganizationAdmin;
}

export default function OrganizationSection({ organizationAdmin }: OrganizationSectionProps) {
  const { organization } = organizationAdmin;

  const states = useMemo(() => {
    const abbreviations = organization.organizationUsers
      .map((orgUser) => orgUser.user.superviseeProfile?.licensureUsState?.abbreviation)
      .filter((abbr): abbr is string => Boolean(abbr));

    return Array.from(new Set(abbreviations)).sort((a, b) => a.localeCompare(b));
  }, [organization.organizationUsers]);

  return (
    <Section title="Employer">
      <MoSpacing y={1}>
        <SectionItem label="Name">
          <ZeusOrganizationLink organization={organization} />
        </SectionItem>
        {organization.accountManagerAdmin && (
          <SectionItem label="Account manager">
            {organization.accountManagerAdmin.fullName}
          </SectionItem>
        )}
        {states.length > 0 && (
          <SectionItem label="Operates in">
            <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
              {states.map((abbreviation) => (
                <MoChip key={abbreviation} label={abbreviation} size="sm" color="gray" />
              ))}
            </Box>
          </SectionItem>
        )}
        {organization.salesforceAccountUrl && (
          <SectionItem label="Salesforce">
            <MoExternalLink href={organization.salesforceAccountUrl} openInNewTab>
              View account
            </MoExternalLink>
          </SectionItem>
        )}
      </MoSpacing>
    </Section>
  );
}
